// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Shubham Dixit <shubham@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Settings,
  Users,
  Activity,
  Clock,
  Bell,
  Shield,
  UserPlus,
  Building2,
  ToggleLeft,
  FileText,
  Database,
  Mail,
  MessageSquare,
  ShieldCheck,
  Smartphone,
  Monitor,
  LogOut,
  CheckCircle2,
  Sun,
  Moon,
  Palette,
  Globe,
  Check,
  Upload,
  Network,
  RefreshCw,
} from 'lucide-react'
import { useTheme } from '@/context/theme-context'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { PersonAvatar } from '@/components/ui/person-avatar'
import { i18n, LANG_KEY, RTL_LANGS } from '@/i18n/index'
import { apiFetch } from '@/lib/api'
import BulkImportSection from '@/components/BulkImportSection'

// Real provider roster row from GET /v1/dashboard → providersRegistered.providers.
interface RosterProvider {
  id: string
  email: string
  role: string
  status: string
}

function humanizeRole(s: string): string {
  if (!s) return ''
  const spaced = s.replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

const LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
] as const

function getStoredLang(): string {
  return sessionStorage.getItem(LANG_KEY) ?? 'en'
}

type Tab = 'admin' | 'patients' | 'settings'

function GradientStatCard({
  icon: Icon,
  value,
  label,
  accent = false,
}: {
  icon: React.ElementType
  value: string
  label: string
  accent?: boolean
}) {
  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0d52a0] via-[#1a70d8] to-[#0ea5e9] dark:from-[#012031] dark:via-[#033C85] dark:to-[#006EF9] p-5 flex flex-col gap-3">
      <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/5 blur-xl pointer-events-none" />
      <div
        className={[
          'h-10 w-10 rounded-xl flex items-center justify-center',
          accent ? 'bg-[#02C7C3]/25' : 'bg-white/15',
        ].join(' ')}
      >
        <Icon className={['h-5 w-5', accent ? 'text-[#02C7C3]' : 'text-white'].join(' ')} />
      </div>
      <div>
        <p className="text-2xl font-bold text-white leading-none">{value}</p>
        <p className="text-white/60 text-xs mt-1">{label}</p>
      </div>
    </div>
  )
}

function SectionCard({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ElementType
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="bg-card rounded-2xl border border-border overflow-hidden">
      <div className="flex items-center gap-3 px-6 py-4 border-b border-border bg-secondary/50">
        <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon className="h-4 w-4 text-primary" />
        </div>
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      </div>
      <div className="p-6">{children}</div>
    </div>
  )
}

const HIE_SCOPE_KEYS = [
  'conditions',
  'medications',
  'allergies',
  'labs',
  'immunizations',
] as const

type HieScopeKey = (typeof HIE_SCOPE_KEYS)[number]

function HieConfigCard() {
  const { t } = useTranslation()
  const [enabled, setEnabled] = useState(true)
  const [freshness, setFreshness] = useState('24h')
  const [scope, setScope] = useState<Record<HieScopeKey, boolean>>({
    conditions: true,
    medications: true,
    allergies: true,
    labs: true,
    immunizations: false,
  })

  const scopeLabels: Record<HieScopeKey, string> = {
    conditions: t('settings.hie.scopeConditions'),
    medications: t('settings.hie.scopeMedications'),
    allergies: t('settings.hie.scopeAllergies'),
    labs: t('settings.hie.scopeLabs'),
    immunizations: t('settings.hie.scopeImmunizations'),
  }

  return (
    <SectionCard icon={Network} title={t('settings.hie.title')}>
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between py-2 px-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
              {t('settings.hie.statusConnected')} · Particle Health
            </span>
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <RefreshCw className="h-3 w-3" />
            {t('settings.hie.lastSync')} 2h ago
          </div>
        </div>

        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-foreground">{t('settings.hie.enableFetch')}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{t('settings.hie.enableFetchDesc')}</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-0.5">
            <input
              type="checkbox"
              checked={enabled}
              onChange={e => setEnabled(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-9 h-5 rounded-full bg-muted peer-checked:bg-primary transition-colors after:absolute after:top-0.5 after:left-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all peer-checked:after:translate-x-4" />
          </label>
        </div>

        {enabled && (
          <>
            <div className="flex flex-col gap-1.5 border-t border-border pt-4">
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('settings.hie.freshnessWindow')}
              </label>
              <p className="text-xs text-muted-foreground -mt-0.5">{t('settings.hie.freshnessWindowDesc')}</p>
              <select
                value={freshness}
                onChange={e => setFreshness(e.target.value)}
                className="h-11 rounded-xl border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="1h">1 hour</option>
                <option value="4h">4 hours</option>
                <option value="12h">12 hours</option>
                <option value="24h">24 hours</option>
                <option value="48h">48 hours</option>
                <option value="72h">72 hours</option>
              </select>
            </div>

            <div className="flex flex-col gap-3 border-t border-border pt-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('settings.hie.dataScope')}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">{t('settings.hie.dataScopeDesc')}</p>
              </div>
              {HIE_SCOPE_KEYS.map(key => (
                <label key={key} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={scope[key]}
                    onChange={e => setScope(prev => ({ ...prev, [key]: e.target.checked }))}
                    className="h-4 w-4 accent-primary"
                  />
                  <span className="text-sm text-foreground">{scopeLabels[key]}</span>
                </label>
              ))}
            </div>
          </>
        )}

        <Button variant="accent" size="sm">{t('settings.hie.save')}</Button>
      </div>
    </SectionCard>
  )
}

function AdminContent() {
  const { t } = useTranslation()
  const [totalPatients, setTotalPatients] = useState<number | null>(null)
  const [providers, setProviders] = useState<RosterProvider[]>([])

  // Real org stats + roster — no hardcoded counts. Total from the dashboard
  // summary; providers from the registered roster (same source as ProvidersPage).
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const [sumRes, dashRes] = await Promise.all([
          apiFetch('/v1/dashboard/summary'),
          apiFetch('/v1/dashboard'),
        ])
        if (cancelled) return
        if (sumRes.ok) {
          const s = (await sumRes.json()) as { totalPatients?: number }
          setTotalPatients(s.totalPatients ?? 0)
        }
        if (dashRes.ok) {
          const d = (await dashRes.json()) as { providersRegistered?: { providers?: RosterProvider[] } }
          setProviders(d.providersRegistered?.providers ?? [])
        }
      } catch {
        // leave nulls → stats render "—"; roster renders its empty state.
      }
    })()
    return () => { cancelled = true }
  }, [])

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <GradientStatCard icon={Users} value={String(providers.length)} label={t('settings.activeProviders')} accent />
        <GradientStatCard icon={Activity} value={totalPatients === null ? '—' : String(totalPatients)} label={t('settings.totalPatients')} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard icon={Building2} title={t('settings.clinicInfo')}>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('settings.clinicName')}
              </label>
              <Input placeholder={t('settings.clinicName')} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('settings.city')}
                </label>
                <Input placeholder={t('settings.city')} />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('settings.state')}
                </label>
                <Input placeholder={t('settings.state')} />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('settings.phone')}
              </label>
              <Input placeholder={t('settings.phone')} />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('settings.timezone')}
              </label>
              <Input placeholder="—" readOnly className="opacity-70 cursor-not-allowed" />
            </div>
            <Button variant="accent" size="sm">{t('settings.saveChanges')}</Button>
          </div>
        </SectionCard>

        <SectionCard icon={UserPlus} title={t('settings.providerRoster')}>
          <div className="flex flex-col gap-3">
            {providers.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">No providers registered yet.</p>
            ) : providers.map(p => (
              <div
                key={p.id}
                className="flex items-center justify-between py-3 px-4 rounded-xl bg-secondary/60 hover:bg-secondary transition-colors"
              >
                <div className="flex items-center gap-3">
                  <PersonAvatar seed={p.email} size={32} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate max-w-[220px]">{p.email}</p>
                    <p className="text-xs text-muted-foreground">{humanizeRole(p.role)}</p>
                  </div>
                </div>
                <Badge variant={p.status === 'active' ? 'completed' : 'default'}>
                  {p.status === 'active' ? t('settings.active') : t('settings.inactive')}
                </Badge>
              </div>
            ))}
            <Button variant="outline" size="sm">
              <UserPlus className="h-3.5 w-3.5 mr-2" />
              {t('settings.inviteProvider')}
            </Button>
          </div>
        </SectionCard>

        <SectionCard icon={Shield} title={t('settings.accessControl')}>
          <div className="flex flex-col gap-4">
            {[
              { role: 'Admin', desc: 'Full access to all settings and patient data', enabled: true },
              { role: 'Provider', desc: 'Access to assigned patients and visit prep notes', enabled: true },
              { role: 'Coordinator', desc: 'Schedule appointments and manage patient links', enabled: false },
              { role: 'Read Only', desc: 'View-only access to dashboards and reports', enabled: false },
            ].map(r => (
              <div key={r.role} className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-foreground">{r.role}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{r.desc}</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-0.5">
                  <input type="checkbox" defaultChecked={r.enabled} className="sr-only peer" />
                  <div className="w-9 h-5 rounded-full bg-muted peer-checked:bg-primary transition-colors after:absolute after:top-0.5 after:left-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all peer-checked:after:translate-x-4" />
                </label>
              </div>
            ))}
          </div>
        </SectionCard>

        <HieConfigCard />

      </div>
    </div>
  )
}

function PatientsContent() {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <GradientStatCard icon={Clock} value="72h" label={t('settings.defaultLinkExpiry')} accent />
        <GradientStatCard icon={Bell} value="2×" label={t('settings.remindersPerPatient')} />
        <GradientStatCard icon={Shield} value="94%" label={t('settings.consentRate')} accent />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard icon={ToggleLeft} title={t('settings.selfEvalSettings')}>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('settings.linkExpiry')}
              </label>
              <select className="h-11 rounded-xl border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring">
                <option>24 hours</option>
                <option>48 hours</option>
                <option selected>72 hours</option>
                <option>7 days</option>
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('settings.reminderFreq')}
              </label>
              <select className="h-11 rounded-xl border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring">
                <option>Once (on send)</option>
                <option selected>Twice (on send + 24h before)</option>
                <option>Three times</option>
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('settings.reminderChannel')}
              </label>
              <div className="flex flex-col gap-2">
                {['Email', 'SMS', 'Both'].map((ch, i) => (
                  <label key={ch} className="flex items-center gap-2.5 cursor-pointer">
                    <input type="radio" name="channel" defaultChecked={i === 2} className="accent-primary" />
                    <span className="text-sm text-foreground">{ch}</span>
                  </label>
                ))}
              </div>
            </div>
            <Button variant="accent" size="sm">{t('settings.save')}</Button>
          </div>
        </SectionCard>

        <SectionCard icon={Mail} title={t('settings.notifTemplates')}>
          <div className="flex flex-col gap-4">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('settings.emailLabel')}</p>
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs text-muted-foreground">Subject</label>
                  <Input defaultValue="Complete your visit prep for {appointment_date}" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs text-muted-foreground">Preview</label>
                  <div className="rounded-xl border border-border bg-secondary/50 px-4 py-3 text-xs text-muted-foreground leading-relaxed">
                    Hi {'{first_name}'}, your appointment with {'{provider}'} is on{' '}
                    {'{appointment_date}'}. Please complete your health check-in using the link below
                    before your visit.
                  </div>
                </div>
              </div>
            </div>
            <div className="border-t border-border pt-4">
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('settings.smsLabel')}</p>
              </div>
              <Input defaultValue="Hi {first_name}, complete your visit prep: {link}" />
            </div>
            <Button variant="outline" size="sm">{t('settings.editTemplates')}</Button>
          </div>
        </SectionCard>

        <SectionCard icon={FileText} title={t('settings.consentPrivacy')}>
          <div className="flex flex-col gap-4">
            {[
              { key: 'consentCheck1' as const, defaultChecked: true },
              { key: 'consentCheck2' as const, defaultChecked: true },
              { key: 'consentCheck3' as const, defaultChecked: false },
            ].map(item => (
              <label key={item.key} className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  defaultChecked={item.defaultChecked}
                  className="h-4 w-4 accent-primary mt-0.5 shrink-0"
                />
                <span className="text-sm text-foreground leading-snug">{t(`settings.${item.key}`)}</span>
              </label>
            ))}
            <div className="flex flex-col gap-1.5 pt-2 border-t border-border">
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('settings.consentFormUrl')}
              </label>
              <Input defaultValue="https://queryhealth.com/consent/v2" />
            </div>
            <Button variant="accent" size="sm">{t('settings.save')}</Button>
          </div>
        </SectionCard>

        <SectionCard icon={Database} title={t('settings.dataRetention')}>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('settings.prepNoteRetention')}
              </label>
              <select className="h-11 rounded-xl border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring">
                <option>30 days</option>
                <option>90 days</option>
                <option selected>1 year</option>
                <option>3 years</option>
                <option>7 years (HIPAA max)</option>
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('settings.archiveAfter')}
              </label>
              <select className="h-11 rounded-xl border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring">
                <option>6 months</option>
                <option selected>12 months</option>
                <option>24 months</option>
                <option>Never</option>
              </select>
            </div>
            <div className="flex items-start gap-3 pt-2 border-t border-border">
              <div className="h-8 w-8 rounded-lg bg-destructive/10 flex items-center justify-center shrink-0">
                <Database className="h-4 w-4 text-destructive" />
              </div>
              <div>
                <p className="text-xs font-medium text-foreground">{t('settings.dataExport')}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t('settings.dataExportDesc')}
                </p>
                <button className="text-xs text-primary hover:text-primary/80 mt-1.5 transition-colors font-medium">
                  {t('settings.requestExport')}
                </button>
              </div>
            </div>
            <Button variant="accent" size="sm">{t('settings.save')}</Button>
          </div>
        </SectionCard>
      </div>

      <SectionCard icon={Upload} title={t('settings.bulkImport.title')}>
        <BulkImportSection />
      </SectionCard>
    </div>
  )
}

function MySettingsContent() {
  const { t } = useTranslation()
  const { theme, toggleTheme } = useTheme()
  const [langCode, setLangCode] = useState<string>(getStoredLang)

  function handleLangChange(code: string) {
    sessionStorage.setItem(LANG_KEY, code)
    setLangCode(code)
    void i18n.changeLanguage(code)
    document.documentElement.dir = RTL_LANGS.has(code) ? 'rtl' : 'ltr'
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <GradientStatCard icon={CheckCircle2} value={t('settings.accountActive')} label={t('settings.accountStatus')} accent />
        <GradientStatCard icon={Clock} value="Today 9:24 AM" label={t('settings.lastLogin')} />
        <GradientStatCard icon={ShieldCheck} value={t('settings.twoFAEnabled')} label={t('settings.twoFALabel')} accent />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="flex flex-col gap-6">
          <SectionCard icon={Users} title={t('settings.profileInfo')}>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('settings.firstName')}
                </label>
                <Input defaultValue="Miles" readOnly className="opacity-70 cursor-not-allowed" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('settings.lastName')}
                </label>
                <Input defaultValue="O'Brien" readOnly className="opacity-70 cursor-not-allowed" />
              </div>
              <div className="flex flex-col gap-1.5 col-span-2">
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('settings.email')}
                </label>
                <Input
                  defaultValue="miles@queryhealth.com"
                  readOnly
                  className="opacity-70 cursor-not-allowed"
                />
              </div>
              <div className="flex flex-col gap-1.5 col-span-2">
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('settings.role')}
                </label>
                <Input defaultValue="Admin" readOnly className="opacity-70 cursor-not-allowed" />
              </div>
            </div>
          </SectionCard>

          <SectionCard icon={Bell} title={t('settings.notifications')}>
            <div className="flex flex-col gap-4">
              {([
                { key: 'notifEmail',        defaultChecked: true  },
                { key: 'notifPrepComplete', defaultChecked: false },
                { key: 'notifWeeklyDigest', defaultChecked: true  },
                { key: 'notifSecurity',     defaultChecked: true  },
              ] as const).map(item => (
                <label key={item.key} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    defaultChecked={item.defaultChecked}
                    className="h-4 w-4 accent-primary"
                  />
                  <span className="text-sm text-foreground">{t(`settings.${item.key}`)}</span>
                </label>
              ))}
              <Button variant="accent" size="sm">
                {t('settings.savePreferences')}
              </Button>
            </div>
          </SectionCard>
        </div>

        <div className="flex flex-col gap-6">
          <SectionCard icon={Palette} title={t('settings.appearance')}>
            <div className="flex flex-col gap-5">
              {/* Theme toggle */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-xl bg-secondary flex items-center justify-center">
                    {theme === 'dark' ? (
                      <Moon className="h-4 w-4 text-primary" />
                    ) : (
                      <Sun className="h-4 w-4 text-primary" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {theme === 'dark' ? t('settings.darkMode') : t('settings.lightMode')}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {theme === 'dark' ? t('settings.switchToLight') : t('settings.switchToDark')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={toggleTheme}
                  className={[
                    'relative inline-flex h-7 w-13 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200',
                    theme === 'dark' ? 'bg-primary' : 'bg-muted',
                  ].join(' ')}
                  role="switch"
                  aria-checked={theme === 'dark'}
                  aria-label={t('settings.toggleDarkMode')}
                >
                  <span
                    className={[
                      'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200 mt-px',
                      theme === 'dark' ? 'translate-x-6' : 'translate-x-0.5',
                    ].join(' ')}
                  />
                </button>
              </div>

              {/* Language selector */}
              <div className="border-t border-border pt-5">
                <div className="flex items-center gap-2 mb-3">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('settings.interfaceLanguage')}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {LANGUAGES.map(lang => {
                    const selected = lang.code === langCode
                    return (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => handleLangChange(lang.code)}
                        className={[
                          'flex items-center justify-between px-3 py-2.5 rounded-xl border text-left transition-all',
                          selected
                            ? 'border-primary bg-primary/8 dark:bg-primary/15'
                            : 'border-border bg-secondary/60 hover:border-primary/40 hover:bg-secondary',
                        ].join(' ')}
                      >
                        <div>
                          <p className={['text-sm font-medium leading-snug', selected ? 'text-primary' : 'text-foreground'].join(' ')}>
                            {lang.nativeName}
                          </p>
                          <p className="text-xs text-muted-foreground">{lang.name}</p>
                        </div>
                        {selected && (
                          <Check className="h-4 w-4 text-primary flex-shrink-0 ml-2" />
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>
          </SectionCard>

          <SectionCard icon={Shield} title={t('settings.changePassword')}>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('settings.currentPassword')}
                </label>
                <Input type="password" placeholder="••••••••" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('settings.newPassword')}
                </label>
                <Input type="password" placeholder="••••••••" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('settings.confirmNewPassword')}
                </label>
                <Input type="password" placeholder="••••••••" />
              </div>
              <Button variant="accent" size="sm">
                {t('settings.updatePassword')}
              </Button>
            </div>
          </SectionCard>

          <SectionCard icon={ShieldCheck} title={t('settings.securitySessions')}>
            <div className="flex flex-col gap-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-foreground">{t('settings.twoFactorAuth')}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t('settings.twoFactorDesc')}
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-0.5">
                  <input type="checkbox" defaultChecked className="sr-only peer" />
                  <div className="w-9 h-5 rounded-full bg-muted peer-checked:bg-primary transition-colors after:absolute after:top-0.5 after:left-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all peer-checked:after:translate-x-4" />
                </label>
              </div>

              <div className="border-t border-border pt-4 flex flex-col gap-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('settings.activeSessions')}
                </p>
                {[
                  {
                    icon: Monitor,
                    device: 'MacBook Pro',
                    location: 'San Francisco, CA',
                    time: 'Current session',
                    current: true,
                  },
                  {
                    icon: Smartphone,
                    device: 'iPhone 15 Pro',
                    location: 'San Francisco, CA',
                    time: '2 hours ago',
                    current: false,
                  },
                  {
                    icon: Monitor,
                    device: 'Windows PC',
                    location: 'Oakland, CA',
                    time: 'Yesterday 4:12 PM',
                    current: false,
                  },
                ].map(session => (
                  <div
                    key={session.device}
                    className="flex items-center gap-3 py-2.5 px-3 rounded-xl bg-secondary/60"
                  >
                    <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <session.icon className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-foreground">{session.device}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {session.location} · {session.time}
                      </p>
                    </div>
                    {session.current && (
                      <span className="text-xs text-[#02C7C3] font-medium shrink-0">{t('settings.currentSession')}</span>
                    )}
                  </div>
                ))}
              </div>

              <Button variant="destructive-outline" size="sm">
                <LogOut className="h-3.5 w-3.5 mr-2" />
                {t('settings.signOutAll')}
              </Button>
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  )
}

export default function SettingsPage() {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<Tab>('admin')

  const TABS: { id: Tab; label: string }[] = [
    { id: 'admin', label: t('settings.tabAdmin') },
    { id: 'patients', label: t('settings.tabPatients') },
    { id: 'settings', label: t('settings.tabMySettings') },
  ]

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <Settings className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('settings.pageTitle')}</h1>
          <p className="text-sm text-muted-foreground">{t('settings.pageSubtitle')}</p>
        </div>
      </div>

      <div className="bg-card rounded-2xl border border-border p-1 flex gap-1 w-full sm:max-w-xl">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={
              activeTab === tab.id
                ? 'bg-primary text-primary-foreground rounded-xl px-4 sm:px-6 py-2 text-sm font-semibold transition-all flex-1 sm:flex-none'
                : 'text-muted-foreground px-4 sm:px-6 py-2 text-sm hover:text-foreground transition-colors rounded-xl flex-1 sm:flex-none'
            }
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'admin' && <AdminContent />}
      {activeTab === 'patients' && <PatientsContent />}
      {activeTab === 'settings' && <MySettingsContent />}
    </div>
  )
}
